(function(){
  'use strict';

  // ---------------------------------------------------------------------
  // Generic reusable horizontal slider (native scroll-snap + arrow
  // buttons). Shared by the product gallery hero AND any other card
  // slider on the site (e.g. "More to Explore") so we never ship a
  // second slider implementation or a separate library -- everything
  // that needs a slider calls this same function.
  // Markup contract: root has [data-slider-track] (the scroll container)
  // and optional [data-slider-prev] / [data-slider-next] buttons.
  // Mobile swipe comes for free from native scroll-snap; no JS needed
  // for touch at all.
  // ---------------------------------------------------------------------
  window.initTrackSlider = function(root){
    if(!root) return null;
    var track = root.querySelector('[data-slider-track]');
    if(!track) return null;
    var prevBtn = root.querySelector('[data-slider-prev]');
    var nextBtn = root.querySelector('[data-slider-next]');

    function pageAmount(){
      var first = track.children[0];
      if(!first) return track.clientWidth;
      var w = first.getBoundingClientRect().width;
      var cs = getComputedStyle(track);
      var gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
      var perView = Math.max(1, Math.floor((track.clientWidth + gap) / (w + gap)));
      return (w + gap) * perView;
    }

    if(prevBtn) prevBtn.addEventListener('click', function(){
      track.scrollBy({ left: -pageAmount(), behavior: 'smooth' });
    });
    if(nextBtn) nextBtn.addEventListener('click', function(){
      track.scrollBy({ left: pageAmount(), behavior: 'smooth' });
    });

    function syncArrows(){
      if(prevBtn) prevBtn.disabled = track.scrollLeft <= 4;
      if(nextBtn) nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
    }
    track.addEventListener('scroll', syncArrows, { passive: true });
    syncArrows();

    return { refresh: syncArrows };
  };

  function initGallery(root){
    var track = root.querySelector('[data-gallery-track]');
    var slides = track ? Array.prototype.slice.call(track.children) : [];
    if(!track || slides.length < 1) return null;
    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-dots] [data-index]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    var thumbZooms = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb-zoom]'));
    var zoomBtn = root.querySelector('[data-gallery-zoom]');
    var current = 0;
    var warmed = {};

    function warm(i){
      if(warmed[i]) return;
      var slide = slides[i];
      var img = slide && slide.querySelector('img');
      var zoomSrc = img && img.getAttribute('data-zoom-src');
      if(!zoomSrc) return;
      warmed[i] = true;
      var pre = new Image();
      pre.src = zoomSrc;
    }

    function setActive(i){
      current = i;
      dots.forEach(function(d){ if(parseInt(d.getAttribute('data-index'),10) === i){ d.setAttribute('data-active',''); } else { d.removeAttribute('data-active'); } });
      thumbs.forEach(function(t){ if(parseInt(t.getAttribute('data-index'),10) === i){ t.setAttribute('data-active',''); } else { t.removeAttribute('data-active'); } });
      warm(i);
    }

    function goTo(i, behavior){
      var n = slides.length;
      var next = (i + n) % n;
      track.scrollTo({ left: track.clientWidth * next, behavior: behavior || 'smooth' });
    }

    if(prevBtn) prevBtn.addEventListener('click', function(){ goTo(current - 1); });
    if(nextBtn) nextBtn.addEventListener('click', function(){ goTo(current + 1); });

    thumbs.forEach(function(t){
      t.addEventListener('click', function(){ goTo(parseInt(t.getAttribute('data-index'), 10)); });
    });
    dots.forEach(function(d){
      d.addEventListener('click', function(){ goTo(parseInt(d.getAttribute('data-index'), 10)); });
    });
    thumbZooms.forEach(function(z){
      z.addEventListener('click', function(e){
        e.stopPropagation();
        lbOpen(parseInt(z.getAttribute('data-index'), 10));
      });
    });

    // Tapping/clicking the big hero photo itself opens the full popup --
    // this is the natural "I want to see this bigger" gesture, so the
    // popup isn't only reachable via the small zoom-icon button.
    slides.forEach(function(slide, i){
      var img = slide.querySelector('img');
      if(!img) return;
      img.addEventListener('click', function(){ lbOpen(i); });
    });

    var ioReady = false;
    if('IntersectionObserver' in window){
      var io = new IntersectionObserver(function(entries){
        if(!ioReady) return;
        entries.forEach(function(entry){
          if(entry.isIntersecting && entry.intersectionRatio > 0.6){
            setActive(slides.indexOf(entry.target));
          }
        });
      }, { root: track, threshold: 0.6 });
      slides.forEach(function(s){ io.observe(s); });
    }

    if(zoomBtn){
      zoomBtn.addEventListener('click', function(){
        lbOpen(current);
      });
    }

    setActive(0);

    // Force-hold the track at slide 0 briefly after load. Some mobile browsers
    // (scroll anchoring / focus restoration on horizontally-scrolling flex containers) can drift
    // the scroll position away from 0 during initial paint; this brute-forces it back every frame
    // until layout has settled, then hands control to the IntersectionObserver. Capped at a fixed
    // frame count (not wall-clock time) so it can't run longer than necessary -- reading
    // scrollLeft every frame forces a layout reflow, and drift in practice always resolves within
    // the first handful of frames, so 1000ms of polling was doing ~55 wasted reflow checks for no
    // benefit. 20 frames (~330ms at 60fps) keeps the same protection at a fraction of the cost.
    var holdFrames = 20;
    (function hold(){
      if(track.scrollLeft !== 0) track.scrollLeft = 0;
      holdFrames -= 1;
      if(holdFrames > 0){
        requestAnimationFrame(hold);
      } else {
        ioReady = true;
      }
    })();
  }

  document.querySelectorAll('[data-gallery]').forEach(initGallery);

  // ---------------------------------------------------------------------
  // Lightbox: true full-screen photo viewer -- one big photo, a counter,
  // prev/next arrows and a single horizontal thumbnail strip along the
  // bottom, edge to edge on every screen size (no floating card with
  // dead space around it). Swipe left/right changes photos and swipe
  // down closes it on touch; both handled with plain pointer events, no
  // external libraries.
  //
  // Zoom: click (mouse) or double-tap (touch) the photo to zoom in to
  // ~2.3x, centered on wherever was clicked/tapped -- click or
  // double-tap again to zoom back out. Pinch with two fingers zooms
  // continuously on touch. Whenever the photo is zoomed in, press-and-
  // drag (mouse) or a one-finger drag (touch) pans around the image
  // instead of changing slides or closing the popup, so the same
  // gesture never means two different things at once. Changing slides
  // (arrow, thumbnail or swipe) always resets zoom back to fit.
  // ---------------------------------------------------------------------
  var lightbox = document.querySelector('[data-gallery-lightbox]');
  if(lightbox){
    var lbTrack = lightbox.querySelector('[data-gallery-lightbox-track]');
    var lbCounter = lightbox.querySelector('[data-gallery-lightbox-counter]');
    var lbSlides = lbTrack ? Array.prototype.slice.call(lbTrack.children) : [];
    var lbPrev = lightbox.querySelector('[data-gallery-lightbox-prev]');
    var lbNext = lightbox.querySelector('[data-gallery-lightbox-next]');
    var lbClose = lightbox.querySelector('[data-gallery-lightbox-close]');
    var lbThumbs = Array.prototype.slice.call(lightbox.querySelectorAll('[data-gallery-lightbox-thumb]'));
    var lbIndex = 0;
    var lbN = lbSlides.length;

    var ZOOM_STEP = 2.3;
    var ZOOM_MAX = 3.2;
    var zoom = { scale: 1, x: 0, y: 0 };

    function activeImg(){
      var s = lbSlides[lbIndex];
      return s ? s.querySelector('img') : null;
    }

    function applyZoom(){
      var img = activeImg();
      if(!img) return;
      img.style.transform = zoom.scale > 1.001
        ? 'translate(' + zoom.x + 'px,' + zoom.y + 'px) scale(' + zoom.scale + ')'
        : '';
      img.classList.toggle('is-zoomed', zoom.scale > 1.02);
    }

    function clampPan(img, scale, x, y){
      // img.clientWidth/Height are the laid-out (fitted, pre-transform)
      // box -- CSS transforms don't affect layout size, so this stays
      // stable no matter the current zoom, no re-measuring tricks needed.
      var maxX = Math.max(0, (img.clientWidth * (scale - 1)) / 2);
      var maxY = Math.max(0, (img.clientHeight * (scale - 1)) / 2);
      return {
        x: Math.max(-maxX, Math.min(maxX, x)),
        y: Math.max(-maxY, Math.min(maxY, y))
      };
    }

    function setZoom(scale, x, y){
      var img = activeImg();
      if(!img) return;
      var clamped = clampPan(img, scale, x || 0, y || 0);
      zoom.scale = scale;
      zoom.x = clamped.x;
      zoom.y = clamped.y;
      applyZoom();
    }

    function resetZoom(animated){
      var img = activeImg();
      if(img) img.style.transition = animated ? 'transform .2s ease' : 'none';
      setZoom(1, 0, 0);
      if(img){
        window.setTimeout(function(){ img.style.transition = ''; }, animated ? 220 : 0);
      }
    }

    function toggleZoom(clientX, clientY){
      var img = activeImg();
      if(!img) return;
      img.style.transition = 'transform .2s ease';
      if(zoom.scale > 1.02){
        setZoom(1, 0, 0);
      } else {
        var rect = img.getBoundingClientRect();
        var offsetX = clientX - (rect.left + rect.width / 2);
        var offsetY = clientY - (rect.top + rect.height / 2);
        setZoom(ZOOM_STEP, -offsetX * (ZOOM_STEP - 1), -offsetY * (ZOOM_STEP - 1));
      }
      window.setTimeout(function(){ if(img) img.style.transition = ''; }, 220);
    }

    function lbRender(fade){
      if(!lbTrack) return;
      lbTrack.style.transition = 'none';
      lbTrack.style.transform = 'translateX(' + (-lbIndex * 100) + '%)';
      if(lbCounter) lbCounter.textContent = (lbIndex + 1) + ' / ' + lbN;
      if(fade){
        var activeSlide = lbSlides[lbIndex];
        if(activeSlide){
          activeSlide.style.transition = 'none';
          activeSlide.style.opacity = '0';
          requestAnimationFrame(function(){
            activeSlide.style.transition = 'opacity .15s ease';
            activeSlide.style.opacity = '1';
          });
        }
      }
      lbThumbs.forEach(function(t){
        var idx = parseInt(t.getAttribute('data-index'), 10);
        if(idx === lbIndex){
          t.setAttribute('data-active', '');
          if(typeof t.scrollIntoView === 'function') t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } else {
          t.removeAttribute('data-active');
        }
      });
    }

    function lbGoTo(i){
      var next = Math.max(0, Math.min(lbN - 1, i));
      resetZoom(false);
      if(next === lbIndex){ lbRender(false); return; }
      lbIndex = next;
      lbRender(true);
    }

    if(lbPrev) lbPrev.addEventListener('click', function(){ lbGoTo(lbIndex - 1); });
    if(lbNext) lbNext.addEventListener('click', function(){ lbGoTo(lbIndex + 1); });
    if(lbClose) lbClose.addEventListener('click', function(){ lightbox.close(); });
    lbThumbs.forEach(function(t){
      t.addEventListener('click', function(){ lbGoTo(parseInt(t.getAttribute('data-index'), 10)); });
    });
    lightbox.addEventListener('keydown', function(e){
      if(e.key === 'ArrowLeft') lbGoTo(lbIndex - 1);
      if(e.key === 'ArrowRight') lbGoTo(lbIndex + 1);
      if(e.key === 'Escape') lightbox.close();
    });

    // Desktop mouse: click the photo to zoom in (centered on the click),
    // click again to zoom back out -- no need to discover a double-click.
    // While zoomed, press and drag to pan around (cursor turns into an
    // open/closed hand, same convention as Google Photos and most native
    // gallery viewers). A drag longer than a few pixels is treated as a
    // pan, not a click, so it doesn't also toggle the zoom on release.
    var mouseDragged = false;
    if(lbTrack){
      lbTrack.addEventListener('mousedown', function(e){
        if(zoom.scale <= 1.02) return;
        e.preventDefault();
        mouseDragged = false;
        var startClientX = e.clientX, startClientY = e.clientY;
        var startPanX = zoom.x, startPanY = zoom.y;
        var img = activeImg();
        if(img) img.classList.add('is-panning');
        function onMove(ev){
          var mdx = ev.clientX - startClientX, mdy = ev.clientY - startClientY;
          if(Math.abs(mdx) > 4 || Math.abs(mdy) > 4) mouseDragged = true;
          setZoom(zoom.scale, startPanX + mdx, startPanY + mdy);
        }
        function onUp(){
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          if(img) img.classList.remove('is-panning');
        }
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      });

      lbTrack.addEventListener('click', function(e){
        if(e.target.tagName !== 'IMG'){
          if(e.target === lbTrack) lightbox.close();
          return;
        }
        if(mouseDragged){ mouseDragged = false; return; }
        toggleZoom(e.clientX, e.clientY);
      });
    }

    window.lbOpen = function(index){
      lbIndex = Math.max(0, Math.min(lbN - 1, index || 0));
      lightbox.showModal();
      // Position instantly (no slide-in animation) before paint.
      requestAnimationFrame(function(){ resetZoom(false); lbRender(false); });
    };

    if(lbTrack && window.PointerEvent){
      var pointers = {}; // pointerId -> {x, y}
      var pointerOrder = [];
      var mode = null; // 'swipe' | 'pan' | 'pinch' | null
      var startX = 0, startY = 0, startTime = 0;
      var dx = 0, dy = 0, axis = null;
      var panStartX = 0, panStartY = 0;
      var pinchStartDist = 0, pinchStartScale = 1;
      var lastTapTime = 0, lastTapX = 0, lastTapY = 0;
      var movedFar = false;

      function dist(a, b){
        return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
      }

      lbTrack.addEventListener('pointerdown', function(e){
        if(e.pointerType === 'mouse') return;
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        if(pointerOrder.indexOf(e.pointerId) === -1) pointerOrder.push(e.pointerId);
        try { lbTrack.setPointerCapture(e.pointerId); } catch(err){}

        if(pointerOrder.length === 2){
          // Second finger down -- switch straight into pinch, whatever
          // the first finger was doing.
          var ids = pointerOrder.slice(-2);
          pinchStartDist = Math.max(1, dist(pointers[ids[0]], pointers[ids[1]]));
          pinchStartScale = zoom.scale;
          mode = 'pinch';
          axis = null;
        } else if(pointerOrder.length === 1){
          movedFar = false;
          startX = e.clientX; startY = e.clientY; startTime = Date.now();
          dx = 0; dy = 0; axis = null;
          if(zoom.scale > 1.02){
            mode = 'pan';
            panStartX = zoom.x; panStartY = zoom.y;
          } else {
            mode = 'swipe';
          }
        }
      });

      lbTrack.addEventListener('pointermove', function(e){
        if(!(e.pointerId in pointers)) return;
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };

        if(mode === 'pinch' && pointerOrder.length >= 2){
          e.preventDefault();
          var ids = pointerOrder.slice(-2);
          var p1 = pointers[ids[0]], p2 = pointers[ids[1]];
          if(!p1 || !p2) return;
          var d = Math.max(1, dist(p1, p2));
          var scale = Math.max(1, Math.min(ZOOM_MAX, pinchStartScale * (d / pinchStartDist)));
          setZoom(scale, zoom.x, zoom.y);
          return;
        }

        if(mode === 'pan'){
          e.preventDefault();
          var rawDx = e.clientX - startX;
          var rawDy = e.clientY - startY;
          setZoom(zoom.scale, panStartX + rawDx, panStartY + rawDy);
          return;
        }

        if(mode === 'swipe'){
          var rawDx2 = e.clientX - startX;
          var rawDy2 = e.clientY - startY;
          if(Math.abs(rawDx2) > 6 || Math.abs(rawDy2) > 6) movedFar = true;

          if(axis === null && (Math.abs(rawDx2) > 8 || Math.abs(rawDy2) > 8)){
            axis = Math.abs(rawDy2) > Math.abs(rawDx2) ? 'y' : 'x';
          }
          if(axis === null) return;
          e.preventDefault();

          if(axis === 'x'){
            dx = rawDx2;
            // Rubber-band resistance at the first/last slide.
            if((lbIndex === 0 && dx > 0) || (lbIndex === lbN - 1 && dx < 0)){
              dx = dx * 0.35;
            }
            lbTrack.style.transition = 'none';
            lbTrack.style.transform = 'translateX(calc(' + (-lbIndex * 100) + '% + ' + dx + 'px))';
          } else {
            dy = Math.max(0, rawDy2);
            var img = activeImg();
            if(img){ img.style.transition = 'none'; img.style.transform = 'translateY(' + dy + 'px)'; }
            lightbox.style.opacity = String(Math.max(0.4, 1 - dy / 350));
          }
        }
      });

      function endPointer(e){
        var wasMode = mode;
        var tapX = pointers[e.pointerId] ? pointers[e.pointerId].x : e.clientX;
        var tapY = pointers[e.pointerId] ? pointers[e.pointerId].y : e.clientY;
        delete pointers[e.pointerId];
        pointerOrder = pointerOrder.filter(function(id){ return id !== e.pointerId; });

        if(wasMode === 'pinch'){
          if(pointerOrder.length < 2){
            mode = pointerOrder.length === 1 ? 'pan' : null;
            if(zoom.scale < 1.05){
              resetZoom(true);
            } else if(mode === 'pan'){
              panStartX = zoom.x; panStartY = zoom.y;
              var remaining = pointers[pointerOrder[0]];
              if(remaining){ startX = remaining.x; startY = remaining.y; }
            }
          }
          return;
        }

        if(wasMode === 'pan'){
          if(pointerOrder.length === 0) mode = null;
          return;
        }

        if(wasMode === 'swipe'){
          if(axis === 'x'){
            var width = lbTrack.clientWidth || 1;
            var elapsed = Math.max(1, Date.now() - startTime);
            var velocity = dx / elapsed;
            var threshold = Math.max(48, width * 0.18);
            var target = lbIndex;
            if(dx <= -threshold || velocity < -0.45){ target = lbIndex + 1; }
            else if(dx >= threshold || velocity > 0.45){ target = lbIndex - 1; }
            lbGoTo(target);
          } else if(axis === 'y'){
            var img = activeImg();
            if(dy > 110){
              if(img){ img.style.transition = 'transform .15s ease, opacity .15s ease'; img.style.transform = 'translateY(' + (dy + 200) + 'px)'; img.style.opacity = '0'; }
              window.setTimeout(function(){ lightbox.close(); }, 120);
            } else if(img){
              img.style.transition = 'transform .2s ease';
              img.style.transform = '';
            }
            lightbox.style.opacity = '';
          } else if(!movedFar){
            // A genuine tap (no swipe axis, negligible movement): check
            // for a double-tap to zoom.
            var now = Date.now();
            var closeEnough = Math.abs(tapX - lastTapX) < 40 && Math.abs(tapY - lastTapY) < 40;
            if(now - lastTapTime < 320 && closeEnough){
              toggleZoom(tapX, tapY);
              lastTapTime = 0;
            } else {
              lastTapTime = now; lastTapX = tapX; lastTapY = tapY;
            }
          }
          axis = null; dx = 0; dy = 0;
          if(pointerOrder.length === 0) mode = null;
        }
      }

      lbTrack.addEventListener('pointerup', endPointer);
      lbTrack.addEventListener('pointercancel', endPointer);

      lightbox.addEventListener('close', function(){
        var img = activeImg();
        if(img){ img.style.transition = ''; img.style.transform = ''; img.style.opacity = ''; }
        lightbox.style.opacity = '';
        zoom.scale = 1; zoom.x = 0; zoom.y = 0;
        pointers = {}; pointerOrder = []; mode = null;
      });
    }
  }
})();
